import React, { useState } from 'react';
import { 
  Pill, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  User,
  Calendar,
  Shield,
  FileText,
  Package,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

interface Prescription {
  id: string;
  patientName: string;
  medicationName: string;
  prescriber: string;
  rxNumber: string;
  quantity: number;
  daysSupply: number;
  refills: number;
  refillsRemaining: number;
  instructions: string;
  sig: string;
  status: 'pending' | 'verified' | 'dispensed' | 'cancelled' | 'on-hold';
  priority: 'routine' | 'urgent' | 'stat';
  createdAt: string;
  dispensedAt?: string;
  pharmacistId?: string;
  technicianId?: string;
  verificationNotes?: string;
  patientInstructions?: string;
  auxiliaryLabels: string[];
  insuranceClaimId?: string;
  copay?: number;
  totalCost?: number;
}

export function Prescriptions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');

  const mockPrescriptions: Prescription[] = [
    {
      id: '1',
      patientName: 'John Smith',
      medicationName: 'Lisinopril 10mg',
      prescriber: 'Dr. Maria Rodriguez',
      rxNumber: 'RX001234',
      quantity: 30,
      daysSupply: 30,
      refills: 3,
      refillsRemaining: 3,
      instructions: 'Take 1 tablet daily',
      sig: '1 tab PO QD',
      status: 'pending',
      priority: 'routine',
      createdAt: '2024-08-20T09:00:00Z',
      auxiliaryLabels: ['Take with food', 'May cause dizziness'],
      copay: 5.00,
      totalCost: 25.00
    },
    {
      id: '2',
      patientName: 'Maria Garcia',
      medicationName: 'Metformin 500mg',
      prescriber: 'Dr. Carlos Lopez',
      rxNumber: 'RX001235',
      quantity: 60,
      daysSupply: 60,
      refills: 2,
      refillsRemaining: 2,
      instructions: 'Take 1 tablet twice daily with meals',
      sig: '1 tab PO BID with meals',
      status: 'verified',
      priority: 'routine',
      createdAt: '2024-08-20T08:30:00Z',
      pharmacistId: 'PH001',
      verificationNotes: 'Dose appropriate for patient weight',
      auxiliaryLabels: ['Take with food', 'Monitor blood sugar'],
      copay: 3.00,
      totalCost: 18.00
    },
    {
      id: '3',
      patientName: 'Carlos Rodriguez',
      medicationName: 'Albuterol HFA 90mcg',
      prescriber: 'Dr. Ana Martinez',
      rxNumber: 'RX001236',
      quantity: 1,
      daysSupply: 30,
      refills: 5,
      refillsRemaining: 5,
      instructions: 'Inhale 2 puffs every 4-6 hours as needed',
      sig: '2 puffs INH Q4-6H PRN',
      status: 'dispensed',
      priority: 'urgent',
      createdAt: '2024-08-20T07:00:00Z',
      dispensedAt: '2024-08-20T10:00:00Z',
      pharmacistId: 'PH001',
      technicianId: 'TECH001',
      verificationNotes: 'Patient education provided on proper inhaler technique',
      patientInstructions: 'Shake well before use. Rinse mouth after use.',
      auxiliaryLabels: ['Shake well', 'Rinse mouth after use'],
      copay: 10.00,
      totalCost: 45.00
    }
  ];

  const filteredPrescriptions = mockPrescriptions.filter(prescription => {
    const matchesSearch = 
      prescription.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prescription.medicationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prescription.rxNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prescription.prescriber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || prescription.status === selectedStatus;
    const matchesPriority = selectedPriority === 'all' || prescription.priority === selectedPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'verified': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'dispensed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'on-hold': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'verified': return <CheckCircle className="w-4 h-4" />;
      case 'dispensed': return <Package className="w-4 h-4" />;
      case 'cancelled': return <AlertTriangle className="w-4 h-4" />;
      case 'on-hold': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'stat': return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'routine': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'stat': return <TrendingUp className="w-4 h-4" />;
      case 'urgent': return <AlertTriangle className="w-4 h-4" />;
      case 'routine': return <CheckCircle className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getStatusCount = (status: string) => {
    return mockPrescriptions.filter(p => p.status === status).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Prescription Management</h1>
          <p className="text-gray-600 mt-2">Process, verify, and dispense prescriptions with full workflow tracking</p>
        </div>
        <button className="bg-gradient-to-r from-[#4B0082] to-[#6B38FB] hover:from-[#6B38FB] hover:to-[#4B0082] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
          <Plus className="w-5 h-5 inline mr-2" />
          New Prescription
        </button>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{getStatusCount('pending')}</div>
          <div className="text-sm text-gray-600">Pending</div>
          <div className="w-full bg-yellow-200 rounded-full h-2 mt-2">
            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${(getStatusCount('pending') / mockPrescriptions.length) * 100}%` }}></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{getStatusCount('verified')}</div>
          <div className="text-sm text-gray-600">Verified</div>
          <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(getStatusCount('verified') / mockPrescriptions.length) * 100}%` }}></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{getStatusCount('dispensed')}</div>
          <div className="text-sm text-gray-600">Dispensed</div>
          <div className="w-full bg-green-200 rounded-full h-2 mt-2">
            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(getStatusCount('dispensed') / mockPrescriptions.length) * 100}%` }}></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{getStatusCount('on-hold')}</div>
          <div className="text-sm text-gray-600">On Hold</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div className="bg-gray-500 h-2 rounded-full" style={{ width: `${(getStatusCount('on-hold') / mockPrescriptions.length) * 100}%` }}></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{mockPrescriptions.length}</div>
          <div className="text-sm text-gray-600">Total</div>
          <div className="w-full bg-[#6B38FB]/20 rounded-full h-2 mt-2">
            <div className="bg-[#6B38FB] h-2 rounded-full" style={{ width: '100%' }}></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Prescriptions</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by patient, medication, or RX number..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="dispensed">Dispensed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="routine">Routine</option>
              <option value="urgent">Urgent</option>
              <option value="stat">Stat</option>
            </select>
          </div>
        </div>
      </div>

      {/* Prescriptions List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Prescription</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPrescriptions.map((prescription) => (
                <tr key={prescription.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-full flex items-center justify-center text-white">
                          <Pill className="w-5 h-5" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-gray-900">{prescription.medicationName}</p>
                          <p className="text-sm text-gray-500">RX: {prescription.rxNumber}</p>
                          <p className="text-sm text-gray-500">{prescription.quantity} units</p>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{prescription.patientName}</p>
                      <p className="text-sm text-gray-500">{prescription.prescriber}</p>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(prescription.priority)}`}>
                          {getPriorityIcon(prescription.priority)}
                          <span className="ml-1 capitalize">{prescription.priority}</span>
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">{prescription.instructions}</p>
                      <p className="text-sm text-gray-500">Sig: {prescription.sig}</p>
                      <p className="text-sm text-gray-500">{prescription.daysSupply} days supply</p>
                      <p className="text-sm text-gray-500">{prescription.refillsRemaining} refills remaining</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(prescription.status)}`}>
                      {getStatusIcon(prescription.status)}
                      <span className="ml-1 capitalize">{prescription.status}</span>
                    </span>
                    {prescription.verificationNotes && (
                      <p className="text-xs text-gray-500 mt-1">{prescription.verificationNotes}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-[#6B38FB]" />
                        {new Date(prescription.createdAt).toLocaleDateString()}
                      </div>
                      {prescription.dispensedAt && (
                        <div className="flex items-center mt-1 text-green-600">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Dispensed: {new Date(prescription.dispensedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      {prescription.status === 'pending' && (
                        <button className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {prescription.status === 'verified' && (
                        <button className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
                          <Package className="w-4 h-4" />
                        </button>
                      )}
                      <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                        <FileText className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
